import { AnchorProvider } from '@coral-xyz/anchor'
import {
  BlockhashWithExpiryBlockHeight,
  Finality,
  // Commitment,
  // ConfirmOptions,
  SendOptions,
  SignatureStatus,
  Transaction,
  TransactionSignature,
  VersionedTransaction
} from '@solana/web3.js'

import { RPCEndpointKey, SUPPORT_RPC_ENDPOINT } from '@/constants/settings'
import { fetcher, reportTxHash } from '@/lib/apiFetcher'

import { confirmTransactionStatus } from '../utils'

// type TradeOptions = {
//   timeout?: number;
//   interval?: number;
//   sendOptions?: SendOptions;
//   minStatus?: Commitment;
//   maxRetries?: number;
// }

// type confirmOptions = {
//   timeout?: number;
//   interval?: number;
//   minStatus?: Commitment;
// }

class TradeProvider extends AnchorProvider {
  constructor(...args: ConstructorParameters<typeof AnchorProvider>) {
    super(...args)
  }

  /**
   * 发送签名后的交易
   * @param signedTx
   * @param options
   * @returns
   */
  public async sendSignedTransaction(
    signedTx: Transaction | VersionedTransaction,
    options?: SendOptions
  ): Promise<TransactionSignature> {
    const defaultOptions = {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
      maxRetries: 3
    }
    const mergedOptions = { ...defaultOptions, ...options }
    const rawTx = signedTx.serialize()
    // console.log('sendSignedTransaction >>>>>>>>>>>>')
    const txHash = await this.connection.sendRawTransaction(
      rawTx,
      mergedOptions as SendOptions
    )
    // console.log('sendSignedTransaction', txHash)
    return txHash
  }

  /**
   * 发送MEV防御交易
   * @param provider
   * @param signedTx
   * @param opts
   * @returns
   */
  public async sendMevProtectionTransaction(
    signedTx: Transaction | VersionedTransaction,
    options?: SendOptions
  ): Promise<TransactionSignature> {
    const defaultOptions = {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
      maxRetries: 3
    }
    const mergedOptions = { ...defaultOptions, ...options }

    const serializeTx = Buffer.from(signedTx.serialize()).toString('base64')
    const quiknodeRPC = SUPPORT_RPC_ENDPOINT.find(
      (item) => item.key === RPCEndpointKey.QuiknodeRPC
    )
    const endpoint = quiknodeRPC?.url
    const payload = {
      jsonrpc: '2.0',
      id: 1,
      method: 'sendTransaction',
      params: [serializeTx, { ...defaultOptions, encoding: 'base64' }]
    }

    let txHash: TransactionSignature | undefined
    let retries = 0
    let error
    while (retries < mergedOptions.maxRetries) {
      try {
        txHash = await fetcher(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }).then((resp) => {
          return resp.result!
        })
        if (txHash) {
          return txHash
        }
      } catch (err) {
        error = err
        // console.warn(`Retry ${retries + 1} failed:`, error);
        await new Promise((resolve) => setTimeout(resolve, 5000))
        retries++
      }
    }
    // console.warn(`Failed to send and confirm transaction after ${mergedOptions.maxRetries} retries`, error);
    return Promise.reject(error)
  }

  /**
   * 发送交易
   * @param tx
   * @param opts
   * @returns
   */
  public async sendTransaction(
    tx: Transaction | VersionedTransaction,
    opts?: {
      sendOptions?: SendOptions // 发送选项
      latestBlockhash?: BlockhashWithExpiryBlockHeight //  区块（便于确认交易）
      finality?: Finality // 最低确认状态 (也可指定onConfirmed | onFinalized来代替)
      timeout?: number // 超时时间
      interval?: number // 轮询间隔
      report?: string // 上报哈希标识（programId）
      onSigned?: (tx: Transaction | VersionedTransaction) => any // 签名完成回调
      onSend?: (txHash: TransactionSignature) => any // 发送完成回调
      onConfirmed?: (
        txHash: TransactionSignature,
        txStatus: SignatureStatus
      ) => any // confirmed回调
      onFinalized?: (
        txHash: TransactionSignature,
        txStatus: SignatureStatus
      ) => any // finalized回调
      onError?: (
        error: Error,
        txHash: TransactionSignature,
        txStatus: SignatureStatus
      ) => any // 异常回调
    }
  ): Promise<TransactionSignature> {
    const {
      sendOptions,
      finality = opts?.onFinalized ? 'finalized' : 'confirmed',
      timeout = 60000,
      onSigned,
      onSend,
      onConfirmed,
      onFinalized,
      onError
    } = opts || {}

    const defaultSendOptions: SendOptions = {
      skipPreflight: false,
      preflightCommitment: 'confirmed',
      maxRetries: 3
    }

    let txHash
    let txStatus
    try {
      const t0 = Date.now()
      const signedTx = await this.wallet.signTransaction(tx)
      onSigned && onSigned(signedTx)

      const rawTx = signedTx.serialize()
      txHash = await this.connection.sendRawTransaction(rawTx, {
        ...defaultSendOptions,
        ...sendOptions
      })
      onSend && onSend(txHash)

      if (finality === 'confirmed' || finality === 'finalized') {
        const t1 = Date.now()
        const timeoutComfirm = timeout - (t1 - t0)
        const options = {
          ...opts,
          'timeout': timeoutComfirm,
          'onConfirmed': onConfirmed
            ? (txHash: TransactionSignature[], txStatus: SignatureStatus[]) => {
              onConfirmed!(txHash[0], txStatus[0])
              }
            : undefined,
          'onFinalized': onFinalized
            ? (txHash: TransactionSignature[], txStatus: SignatureStatus[]) => {
              onFinalized!(txHash[0], txStatus[0])
              }
            : undefined,
          'onError': onError
            ? (
                error: Error,
                txHash: TransactionSignature[],
                txStatus: SignatureStatus[]
              ) => {
                opts?.onError!(error, txHash[0], txStatus[0])
              }
            : undefined
        }
        const [status] = await this.confirmTransactionBatch([txHash], options)
        txStatus = status
      }
    } catch (err: any) {
      onError && onError(err as Error, txHash as TransactionSignature, txStatus as SignatureStatus)
      throw {
        ...err,
        message: err?.message ?? err,
        originError: err,
        handleError: !!onError,
        data: txStatus
      }
    }

    return txHash
  }

  /**
   * 确认交易
   * @param txHash
   * @param opts
   * @returns
   */
  public async confirmTransaction(
    txHash: TransactionSignature,
    opts?: {
      latestBlockhash?: BlockhashWithExpiryBlockHeight
      finality?: Finality
      timeout?: number
      interval?: number
      report?: string
      onConfirmed?: (
        txHash: TransactionSignature,
        txStatus: SignatureStatus
      ) => any
      onFinalized?: (
        txHash: TransactionSignature,
        txStatus: SignatureStatus
      ) => any
      onError?: (
        error: Error,
        txHash: TransactionSignature,
        txStatus: SignatureStatus
      ) => any
    }
  ): Promise<SignatureStatus> {
    const {
      onConfirmed,
      onFinalized,
      onError
    } = opts || {}
    const options = {
      ...opts,
      'onConfirmed': onConfirmed
        ? (txHash: TransactionSignature[], txStatus: SignatureStatus[]) => {
          onConfirmed!(txHash[0], txStatus[0])
          }
        : undefined,
      'onFinalized': onFinalized
        ? (txHash: TransactionSignature[], txStatus: SignatureStatus[]) => {
          onFinalized!(txHash[0], txStatus[0])
          }
        : undefined,
      'onError': onError
        ? (
            error: Error,
            txHash: TransactionSignature[],
            txStatus: SignatureStatus[]
          ) => {
          onError!(error, txHash[0], txStatus[0])
          }
        : undefined
    }
    const [txStatus] = await this.confirmTransactionBatch([txHash], options)
    return txStatus
  }

  /**
   * 批量确认交易
   * @param txHash
   * @param opts
   * @returns
   */
  public async confirmTransactionBatch(
    txHash: TransactionSignature[],
    opts?: {
      latestBlockhash?: BlockhashWithExpiryBlockHeight
      finality?: Finality
      timeout?: number
      interval?: number
      report?: string // 上报哈希标识（programId）
      onConfirmed?: (
        txHash: TransactionSignature[],
        txStatus: SignatureStatus[]
      ) => any
      onFinalized?: (
        txHash: TransactionSignature[],
        txStatus: SignatureStatus[]
      ) => any
      onError?: (
        error: Error,
        txHash: TransactionSignature[],
        txStatus: SignatureStatus[]
      ) => any
    }
  ): Promise<SignatureStatus[]> {
    const {
      latestBlockhash,
      finality = opts?.onFinalized ? 'finalized' : 'confirmed',
      timeout = 60000,
      interval = 3000,
      report,
      onConfirmed,
      onFinalized,
      onError
    } = opts || {}

    let txStatus: SignatureStatus[] = []
    try {
      const t0 = Date.now()

      if (finality === 'confirmed') {
        // 等待 confirmed 状态
        const t1 = Date.now()
        const timeoutComfirm = timeout - (t1 - t0)
        txStatus = await confirmTransactionStatus(txHash, {
          finality: 'confirmed',
          latestBlockhash: latestBlockhash,
          timeout: timeoutComfirm,
          interval
        })
        if (report != undefined) {
          txHash.forEach((hash) => {
            reportTxHash({ txId: hash, programId: report }).catch(
              () => undefined
            )
          })
        }
        onConfirmed && onConfirmed(txHash, txStatus)
      }

      if (finality === 'finalized') {
        const isAllFinalized = txStatus.every(
          (s) => s.confirmationStatus === 'finalized'
        )
        if (isAllFinalized && txStatus.length > 0) {
          onFinalized && onFinalized(txHash, txStatus)
          return txStatus
        }

        // 等待 finalized 状态
        const t2 = Date.now()
        const timeoutFinalize = timeout - (t2 - t0)
        txStatus = await confirmTransactionStatus(txHash, {
          finality: 'finalized',
          latestBlockhash: latestBlockhash,
          timeout: timeoutFinalize,
          interval
        })
        onFinalized && onFinalized(txHash, txStatus)
      }
    } catch (err: any) {
      onError && onError(err as Error, txHash, txStatus)
      throw {
        ...err,
        message: err?.message ?? err,
        handleError: !!onError,
        data: txStatus
      }
    }

    return txStatus
  }
}

export { type SendOptions, TradeProvider }

//   public async confirmTranscation(
//     txHash: TransactionSignature,
//     options?: confirmOptions,
//   ){
//     const defaultOptions = {
//       timeout: 60000,
//       interval: 2000,
//       minStatus: 'confirmed',
//     }
//     const {
//       timeout,
//       interval,
//       minStatus
//     } = { ...defaultOptions, ...options };

//     const statusLevels = ['processed', 'confirmed', 'finalized'];
//     const requiredIndex = statusLevels.indexOf(minStatus!);

//     const start = Date.now();
//     while (Date.now() - start < timeout) {
//       const res = await this.connection.getSignatureStatus(txHash, {
//         searchTransactionHistory: true,
//       });
//       const status = res.value;
//       if (status?.err) {
//         throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
//       }
//       const confirmation = status?.confirmationStatus;
//       if (confirmation) {
//         const currentIndex = statusLevels.indexOf(confirmation);
//         if (currentIndex >= requiredIndex) {
//           return txHash;
//         }
//       }
//       await new Promise((r) => setTimeout(r, interval));
//     }
//     throw new Error(`Timeout waiting for confirmation of tx ${txHash}`);
//   }

//   public async sendAndConfirmTransaction(
//     signedTx: Transaction | VersionedTransaction,
//     options?: TradeOptions
//   ): Promise<TransactionSignature> {
//     const defaultOptions = {
//       timeout: 60000,
//       interval: 2000,
//       sendOptions: {
//         skipPreflight: false,
//         preflightCommitment: 'confirmed'
//       },
//       minStatus: 'confirmed',
//       maxRetries: 3,
//     }
//     const mergedOptions = { ...defaultOptions, ...options };

//     const rawTx = signedTx.serialize();
//     let txHash: TransactionSignature | null = null;
//     let retries = 0;
//     while (retries < mergedOptions.maxRetries) {
//       try {
//         txHash = await this.connection.sendRawTransaction(
//           rawTx,
//           mergedOptions.sendOptions as SendOptions
//         );
//         await this.confirmTranscation(txHash, mergedOptions as confirmOptions)
//         return txHash
//       } catch (err) {
//         console.warn(`Retry ${retries + 1} failed:`, err);
//         retries++;
//       }
//     }
//     throw new Error(`Failed to send and confirm transaction after ${mergedOptions.maxRetries} retries`);
//   }

//   /**
//  * 发送MEV防御交易
//  * @param provider
//  * @param signedTx
//  * @param opts
//  * @returns
//  */
//   public async sendMevProtectionTransaction(
//     signedTx: Transaction | VersionedTransaction,
//     options?: TradeOptions
//   ): Promise<TransactionSignature> {
//     const defaultOptions = {
//       timeout: 60000,
//       interval: 2000,
//       sendOptions: {
//         skipPreflight: false,
//         preflightCommitment: 'confirmed'
//       },
//       minStatus: 'confirmed',
//       maxRetries: 3,
//     }
//     const mergedOptions = { ...defaultOptions, ...options };
//     const serializeTx = Buffer.from(signedTx.serialize()).toString('base64');
//     const quiknodeRPC = SUPPORT_RPC_ENDPOINT.find((item) => item.key === RPCEndpointKey.QuiknodeRPC)
//     const endpoint = quiknodeRPC?.url;
//     const payload = {
//       jsonrpc: "2.0",
//       id: 1,
//       method: "sendTransaction",
//       params: [serializeTx, { ...mergedOptions.sendOptions, "encoding": "base64" }],
//     };

//     let txHash: TransactionSignature | null = null;
//     let retries = 0;
//     while (retries < mergedOptions.maxRetries) {
//       try {
//         txHash = await fetcher(
//           endpoint, {
//           method: 'POST',
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         }).then((resp) =>{
//           return resp.result!
//         })
//         if (!txHash) {
//           throw new Error("Failed to get transaction hash");
//         }
//         await this.confirmTranscation(txHash, mergedOptions as confirmOptions)
//         return txHash
//       } catch (err) {
//         console.warn(`Retry ${retries + 1} failed:`, err);
//         retries++;
//       }
//     }
//     throw new Error(`Failed to send and confirm transaction after ${mergedOptions.maxRetries} retries`);

//     // const response = await fetcher(
//     //   endpoint, {
//     //   method: 'POST',
//     //   headers: {
//     //     "Content-Type": "application/json",
//     //   },
//     //   body: JSON.stringify(payload),
//     // })
//     // return response.result;
//   }
